import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MUITooltip from '@material-ui/core/Tooltip';
import classNames from 'classnames';

import infoIcon from '../../assets/svg/info.svg';
import infoIconLight from '../../assets/svg/info-light.svg';
import infoIconSmall from '../../assets/svg/info-small.svg';
import useStyles from './tooltip.styles';

export function Tooltip({
  text,
  className,
  placement,
  light,
  label,
  onClick,
  onMouseEnter,
  size,
  labelNextToText,
  height,
  smallIcon,
  clickIcon,
  iconSize: icSize,
  formField,
  toggleOnClick,
  ...props
}) {
  const styleClasses = useStyles({ size, height });
  const iconSize = icSize || 'smallIcon';
  const [isOpen, setIsOpen] = useState(false);
  // Whether the open tooltip was pinned by a click (survives mouse-leave) vs.
  // merely hover-opened. A ref, since it shouldn't itself trigger a render —
  // `isOpen` is what drives rendering.
  const isPinned = useRef(false);
  // Controlled mode only when click-toggling is requested; hover keeps working
  // because MUI fires onOpen/onClose on hover/focus.
  // toggleOnClick semantics: click pins the tooltip open (survives mouse-leave);
  // clicking a pinned tooltip closes it; hover open/close still works while unpinned.
  const controlledProps = toggleOnClick
    ? {
        open: isOpen,
        onOpen: () => setIsOpen(true),
        onClose: () => {
          if (!isPinned.current) {
            setIsOpen(false);
          }
        }
      }
    : {};
  const handleClick = (e) => {
    if (toggleOnClick) {
      if (isPinned.current) {
        isPinned.current = false;
        setIsOpen(false);
      } else {
        isPinned.current = true;
        setIsOpen(true);
      }
    }
    onClick(e);
  };
  return (
    <MUITooltip
      classes={{ tooltip: styleClasses.tooltip }}
      title={text}
      placement={placement}
      interactive
      arrow
      {...controlledProps}
    >
      {!label ? (
        <span className={styleClasses.iconContainer} onClick={handleClick} onMouseEnter={onMouseEnter}>
          {smallIcon ? (
            <img
              className={classNames(className, styleClasses[iconSize])}
              src={smallIcon.src || infoIconSmall}
              alt={text}
            />
          ) : (
            <img
              className={classNames(className, styleClasses.info)}
              src={light ? infoIconLight : infoIcon}
              alt={text}
            />
          )}
        </span>
      ) : (
        <div
          {...props}
          className={classNames(styleClasses.labelWrapper, {
            [styleClasses.formField]: formField
          })}
        >
          <span
            className={classNames(className, styleClasses.desktopLabel, {
              [styleClasses.labelNextToText]: labelNextToText
            })}
            onClick={handleClick}
          >
            {label}
            {clickIcon && <img src={clickIcon} alt={text} className={classNames(className, styleClasses[iconSize])} />}
          </span>
        </div>
      )}
    </MUITooltip>
  );
}

Tooltip.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  className: PropTypes.string,
  placement: PropTypes.string,
  light: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  labelNextToText: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'large']),
  height: PropTypes.number,
  smallIcon: PropTypes.object,
  clickIcon: PropTypes.string,
  autoCompleteField: PropTypes.bool,
  formField: PropTypes.bool,
  iconSize: PropTypes.oneOf(['icon16', 'smallIcon', 'icon10']),
  toggleOnClick: PropTypes.bool
};

Tooltip.defaultProps = {
  className: '',
  label: '',
  onClick: () => {},
  onMouseEnter: () => {},
  placement: 'right-start',
  light: false,
  labelNextToText: false,
  size: 'large',
  formField: false,
  toggleOnClick: false
};

export default Tooltip;
